import * as Actions from '../actions/deadlineActions';
import Deadline from '../../entities/deadline';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type DeadlineAction = { 
    type: string; 
    deadline: Deadline | null; 
    deadlineId: string | Deadline; 
    partial: boolean; 
    lastFetch: number; 
    deadlines: Deadline[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Deadline> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Deadline[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Deadline> | null, firmId: string, userId: string, updates?: Deadline[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'deadline', 'deadlines', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Deadline>} data
 * @param {Deadline[]} [updates]
 * @returns {ArrayMap<Deadline>}
 */
function updateStorage(data: ArrayMap<Deadline>, updates?: Deadline[]): ArrayMap<Deadline> {
	if (logging) console.debug('⮂ deadlineReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Deadline[]} [updates]
 * @returns {ArrayMap<Deadline>}
 */
function updateStorageNoReturn(updates?: Deadline[]): void {
	if (logging) console.debug('⮂ deadlineReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Deadline[]} [data]
 * @returns {ArrayMap<Deadline>}
 */
function createState(data?: Deadline[] | ArrayMap<Deadline>): ArrayMap<Deadline> {
	if (!data) return ArrayMapRegistry.clone<Deadline>('deadline', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Deadline>('deadline', data.map(e => e));
    else return ArrayMapRegistry.clone<Deadline>('deadline', data);
}

function deadlines(state: ArrayMap<Deadline>, action: DeadlineAction): ArrayMap<Deadline> {
	if (!state) state = createState();

	let st: ArrayMap<Deadline>;

	switch (action.type) {
		// action.deadline - the new value of deadline with id action.id
		// action.deadlineId - id of the target, used if new value is null
		case Actions.DEADLINE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ deadlineReducer: FETCH_ONE_SUCCESS', action);

			if (action.deadline === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.deadlineId)) {
				st = state.clone();
				st.set(action.deadline);
				// state.clear();

				updateStorageNoReturn([action.deadline]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.deadlines
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.deadlines
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.deadlines
			 !partial, !stateEmpty, fetchEmpty	-> D return deadlines in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge deadlines in state newer than those in action.deadlines
		 */
		case Actions.DEADLINE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.deadlines.length === 0;
			const fetched = action.deadlines.map(deadline => new Deadline({...deadline, persisted: true}));

			if (logging) console.debug(`⮂ deadlineReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} deadlines with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ deadlineReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ deadlineReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Deadline> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Deadline[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(deadline => {
					const current = st.get(deadline.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && deadline.status === 'D') {
							// Remove it from state
							toUpdate.push(deadline);
							st.delete(deadline);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (deadline.addedOn > current.addedOn) {
								toUpdate.push(deadline);
								st.set(deadline);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (deadline.status !== 'D') {
							toUpdate.push(deadline);
							st.set(deadline);
						}
						else if (logging) console.debug('⮂ deadlineReducer: partialMerge: skipping deleted deadline not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ deadlineReducer: empty: deadlines', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ deadlineReducer: fullMerge: deadlines', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localDeadline => {
                    // If this deadline has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localDeadline.persisted) {
                        const fetchedDeadline = st.get(localDeadline.id);
                        if (!fetchedDeadline || localDeadline.addedOn > fetchedDeadline.addedOn) st.set(localDeadline);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.DEADLINE_FETCH_ONE_FAILURE:
			console.error('DEADLINE_FETCH_ONE_FAILURE', action.deadlineId, action.err);

			// No update to storage required
			return state;

		case Actions.DEADLINE_FETCH_FAILURE:
			console.error('DEADLINE_FETCH_FAILURE', action.deadlineId, action.err);

			// No update to storage required
			return state;

		case Actions.DEADLINE_RESTORE: {
		    let deadline = new Deadline({...action.deadline});
            st = state.clone();

            const existing = st.get(deadline.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (deadline.addedOn >= existing.addedOn) {
                    st.set(deadline);
                    return updateStorage(st, [deadline]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (deadline.status === 'D') return state;

                st.set(deadline);
                return updateStorage(st, [deadline]);
            }
        }
            
		case Actions.DEADLINE_SAVE: {
		    // console.debug('⮂ DEADLINE_SAVE reducer', action.deadline?.id);
		    
			// clone the deadline
			let deadline = new Deadline({...action.deadline});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new deadline in the state
			// If this is a create, add to the queue
			const _deadline = st.get(deadline.id);

			if (!_deadline) st.set(deadline);
			else {
				if (deadline.addedOn >= _deadline.addedOn) st.set(deadline);
			}

			return updateStorage(st, [deadline]);
		}

		case Actions.DEADLINE_SAVE_MANY: {
		    if (logging) console.debug('⮂ DEADLINE_SAVE_MANY reducer', action.deadlines.map(deadline => deadline.id).join(','));
		    
			// clone the deadlines
			let toProcess = action.deadlines.map(deadline => new Deadline(deadline));

			st = state.clone();
			// state.clear();

			const updates: Deadline[] = [];

			toProcess.forEach(deadline => {
				// Add or update the new deadline in the state
				// If this is a create, add to the queue
				const existing = st.get(deadline.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (deadline.status !== 'D') {
						st.push(deadline);
						updates.push(deadline);
					}
					else if (logging) console.debug('⮂ DEADLINE_SAVE_MANY: skipping deleted deadline not in state');
				}
				else {
					if (deadline.addedOn >= existing.addedOn) {
						st.set(deadline);
						updates.push(deadline);
					}
					else if (logging) console.debug('⮂ Skipping, existing deadline had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DEADLINE_REMOVE: {
			// clone the deadline
			const deadline = new Deadline({...action.deadline});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(deadline);

			return updateStorage(st, [deadline]);
		}

		case Actions.DEADLINE_RESTORE_FAILURE:
		case Actions.DEADLINE_SAVE_FAILURE: {
		    console.warn('DEADLINE_SAVE_FAILURE reducer', action.deadline?.id);
		    if (!action.deadline?.id) {
				console.warn('DEADLINE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const deadline = ensure(Deadline, action.deadline);
			if (!deadline) {
				console.error('Could not find deadline after call to save');
				return state;
			}

			st = state.clone();

			const _deadlineRef = state.get(deadline.id);

			if (_deadlineRef && (_deadlineRef.addedOn === deadline.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _deadline = new Deadline({..._deadlineRef});
				_deadline.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_deadline.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('deadline', _deadline).then(r => {
						if (r.isErr()) console.error(`⮂ deadlineReducer: failed to save deadline with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ deadlineReducer: saved deadline with fatalError info');
					}).catch(err => console.error(`⮂ deadlineReducer: failed to save deadline with fatalError info: ${err.message}`));

				st.set(_deadline);
			}

			return updateStorage(st);
		}

		case Actions.DEADLINE_SAVE_MANY_FAILURE: {
		    console.warn('DEADLINE_SAVE_MANY_FAILURE reducer', action.deadlines.map(deadline => deadline.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.DEADLINE_RESTORE_SUCCESS:
		case Actions.DEADLINE_SAVE_SUCCESS: {
			if (!action.deadline?.id) {
				console.warn('DEADLINE_SAVE_SUCCESS reducer');
                return state;
			}

			const deadlineRef = state.get(action.deadline?.id);
			if (!deadlineRef) {
				console.warn('Could not find deadline after call to save');
                return state;
			}

			st = state.clone();

			if (action.deadline.addedOn >= deadlineRef.addedOn) {
				st.set(action.deadline);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const deadline = new Deadline({...deadlineRef, persisted: true});
				st.set(deadline);
			}

			return updateStorage(st, [action.deadline]);
		}

		case Actions.DEADLINE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ DEADLINE_SAVE_MANY_SUCCESS reducer', action.deadlines.map(deadline => deadline.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const deadlines: Deadline[] = [];

			action.deadlines.forEach(e => {
				const deadlineRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!deadlineRef) console.warn('Could not find deadline after call to save');
				else if (e.addedOn >= deadlineRef.addedOn) {
				    st.set(e);
				    deadlines.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const deadline = new Deadline({...deadlineRef, persisted: true});
					st.set(deadline);
					deadlines.push(deadline);
				}
			});

			return updateStorage(st, deadlines);
		}

		case Actions.DEADLINE_REMOVE_FAILURE: {
		    console.warn('DEADLINE_REMOVE_FAILURE action', action.deadline?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.DEADLINE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.DEADLINE_RESTORE_MANY: {
			if (logging) console.debug('⮂ DEADLINE_RESTORE_MANY reducer', action.deadlines.map(deadline => deadline.id).join(','));

			const toProcess = action.deadlines.map(deadline => new Deadline({...deadline}));
			st = state.clone();

			const updates: Deadline[] = [];

			toProcess.forEach(deadline => {
				const existing = st.get(deadline.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (deadline.addedOn >= existing.addedOn) {
						st.set(deadline);
						updates.push(deadline);
					}
					else if (logging) console.debug('⮂ DEADLINE_RESTORE_MANY: skipping older version of deadline');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (deadline.status !== 'D') {
						st.set(deadline);
						updates.push(deadline);
					}
					else if (logging) console.debug('⮂ DEADLINE_RESTORE_MANY: skipping deleted deadline not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DEADLINE_REMOVE_MANY: {
			if (logging) console.debug('⮂ DEADLINE_REMOVE_MANY reducer', action.deadlines.map(deadline => deadline.id).join(','));

			const toProcess = action.deadlines.map(deadline => new Deadline({...deadline}));
			st = state.clone();

			toProcess.forEach(deadline => {
				st.delete(deadline);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default deadlines;